'use client'

import { ReactNode, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

const DEFAULT_SELECTOR = '#modal-portal'

interface ClientOnlyPortalProps {
  children: ReactNode
  /** CSS selector for the portal target (e.g. '#modal-portal'). Falls back to document.body when not found. */
  selector?: string
}

/**
 * Renders children into a DOM node via a portal, only after client mount.
 * Avoids "document is not defined" during SSR (Next.js, etc.).
 * Based on Next.js with-portals example and common SSR-safe portal patterns.
 */
export function ClientOnlyPortal({
  children,
  selector = DEFAULT_SELECTOR
}: ClientOnlyPortalProps) {
  const [target, setTarget] = useState<HTMLElement | null>(null)

  useEffect(() => {
    const el = document.querySelector<HTMLElement>(selector) || document.body

    setTarget(el)
  }, [selector])

  if (!target) return null

  return createPortal(children, target)
}
